import React, { useEffect, useState } from "react";
import { Table, Tag, Modal, message, Skeleton, DatePicker } from "antd";
import {
  WalletOutlined, CreditCardOutlined, ThunderboltOutlined, ArrowLeftOutlined,
  SafetyCertificateOutlined, CheckOutlined, StarFilled,
  RocketOutlined, FireOutlined, RiseOutlined, FallOutlined, PieChartOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Header from "./header/header2";
import axios from '../utils/axios';

const { RangePicker } = DatePicker;

const packs = [
  {
    credits: 1000, discount: 0, tier: "Starter",
    icon: <WalletOutlined />,
    iconBg: "bg-slate-100", iconColor: "text-[#1B2A4A]", tierColor: "text-slate-500",
    features: ["1,000 credits", "No expiry", "Instant activation", "Email support"],
  },
  {
    credits: 5000, discount: 10, tier: "Growth",
    icon: <RocketOutlined />,
    iconBg: "bg-amber-50", iconColor: "text-amber-700", tierColor: "text-amber-700",
    features: ["5,000 credits", "10% savings", "No expiry", "Priority support"],
  },
  {
    credits: 10000, discount: 20, tier: "Pro",
    icon: <FireOutlined />,
    iconBg: "bg-slate-100", iconColor: "text-[#1B2A4A]", tierColor: "text-[#1B2A4A]",
    best: true,
    features: ["10,000 credits", "20% savings", "No expiry", "Priority support", "Dedicated manager"],
  },
];

function RingProgress({ percent, color, size = 52, stroke = 5 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (percent / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EDE9F4" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color}
        strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" style={{ transition: "stroke-dashoffset 1s ease" }}
      />
    </svg>
  );
}

export default function Credits() {
  const navigate = useNavigate();
  const organizationId = localStorage.getItem("organizationId");
  const user = JSON.parse(localStorage.getItem("user"));
  const citriot = JSON.parse(localStorage.getItem("citriot")) || {};

  const [tab, setTab] = useState("view");
  const [buyModal, setBuyModal] = useState(false);
  const [availableCredits, setAvailableCredits] = useState(0);
  const [usedCredits, setUsedCredits] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [percent, setPercent] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleErr = (err) => {
    if (err.response?.data?.msg) message.warning(err.response.data.msg);
    else if (err.response?.status === 401) message.error("Not authorised");
    else message.error("Something went wrong");
  };

  const fetchCredits = async () => {
    try {
      setLoadingCredits(true);
      const res = await axios.post("/get-credits-data", { username: user.username, organizationId });
      if (res?.data?.credits) {
        const d = res.data.credits;
        setAvailableCredits(d.availableCredits || 0);
        setUsedCredits(d.usedCredits || 0);
        setPercent(d.totalCredits > 0 ? Math.round((d.usedCredits / d.totalCredits) * 100) : 0);
        setTransactions(
          (d.transactions || []).map((t, i) => ({
            key: i,
            date: new Date(t.date).toLocaleDateString(),
            type: t.type,
            points: t.type === "credit" ? `+${t.points}` : `-${t.points}`,
            desc: t.description,
            // projectName: t.projectName,
          }))
        );
      }
    } catch (e) { handleErr(e); }
    finally { setLoadingCredits(false); }
  };

  const buyCredits = async () => {
    try {
      setLoading(true);
      await axios.post("/buy-credits", { username: user.username, organizationId, selectedPack });
      message.success(`${selectedPack.credits} Credits added to your wallet!`);
      setBuyModal(false);
      fetchCredits();
    } catch (e) { handleErr(e); }
    finally { setLoading(false); }
  };

  const fetchTx = async (range = null) => {
    try {
      setTableLoading(true);
      const payload = { username: user.username, projectName: localStorage.getItem("projectname"), organizationId };
      if (range?.length === 2) {
        payload.startDate = range[0].startOf("day").toDate();
        payload.endDate = range[1].endOf("day").toDate();
      }
      const res = await axios.post("/get-credits-transactions", payload);
      if (res?.data?.transactions)
        setTransactions(
          res.data.transactions.map((t, i) => ({
            key: i,
            date: new Date(t.date).toLocaleDateString(),
            type: t.type,
            points: t.type === "credit" ? `+${t.points}` : `-${t.points}`,
            desc: t.description,
            // projectName: t.projectName,
          }))
        );
    } catch (e) { handleErr(e); }
    finally { setTableLoading(false); }
  };

  useEffect(() => { if (user?.username && organizationId) fetchCredits(); }, []);

  const total = availableCredits + usedCredits;

  const columns = [
    { title: "Date", dataIndex: "date", width: 120 },
    {
      title: "Type", dataIndex: "type", width: 100,
      render: (t) =>
        t === "credit"
          ? <Tag style={{ background: "#E6F4EE", color: "#1A5C38", border: "1px solid #A7D9BB", borderRadius: 20, fontWeight: 600, fontSize: 11, padding: "2px 10px" }}>Credit</Tag>
          : <Tag style={{ background: "#FDECEA", color: "#C0392B", border: "1px solid #F5B7B1", borderRadius: 20, fontWeight: 600, fontSize: 11, padding: "2px 10px" }}>Debit</Tag>,
    },
    {
      title: "Points", dataIndex: "points", width: 110,
      render: (p) => (
        <span className={`font-bold text-[13px] flex items-center gap-1 ${p.startsWith("+") ? "text-[#1A5C38]" : "text-[#C0392B]"}`}>
          {p.startsWith("+") ? <RiseOutlined className="text-[10px]" /> : <FallOutlined className="text-[10px]" />}
          {p}
        </span>
      ),
    },
    // { title: "Project", dataIndex: "projectName", width: 150 },
    { title: "Description", dataIndex: "desc", Ellipsis: true },
  ];

  /* ── Mini stat cards data ── */
  const statCards = [
    {
      label: "Used Credits", val: usedCredits,
      pct: `${percent}% of total`, pctClass: "bg-red-50 text-red-700",
      icon: <FallOutlined />, iconBg: "bg-red-50", iconColor: "text-red-700",
      accentClass: "from-red-600 to-red-400",
      ring: percent, ringColor: "#C0392B",
    },
    {
      label: "Total Credits", val: total,
      pct: "Lifetime credits", pctClass: "bg-slate-100 text-[#1B2A4A]",
      icon: <CreditCardOutlined />, iconBg: "bg-slate-100", iconColor: "text-[#1B2A4A]",
      accentClass: "from-[#1B2A4A] to-[#2C3E6B]",
      ring: 100 - percent, ringColor: "#1B2A4A",
    },
  ];

  const usageLabel = percent < 50 ? "Healthy usage" : percent < 80 ? "Moderate usage" : "High usage";
  const usageLabelClass = percent < 50 ? "bg-green-50 text-green-800" : percent < 80 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700";

  const statTiles = [
    { label: "Usage Rate",    value: `${percent}%`,              icon: <PieChartOutlined />, iconColor: "#B8860B", ring: percent,                                    ringColor: "#B8860B", sub: usageLabel,       subClass: usageLabelClass },
    { label: "Credits Left",  value: availableCredits.toLocaleString(), icon: <ThunderboltOutlined />, iconColor: "#1B2A4A", ring: total > 0 ? Math.round((availableCredits / total) * 100) : 0, ringColor: "#1B2A4A", sub: "Available now",  subClass: "bg-slate-100 text-[#1B2A4A]" },
    { label: "Transactions",  value: transactions.length,        icon: <RiseOutlined />,    iconColor: "#1A5C38", ring: Math.min(transactions.length * 5, 100),      ringColor: "#1A5C38", sub: "Total records", subClass: "bg-green-50 text-green-800" },
  ];

  return (
    <div className="font-sans bg-[#F5F6FA] min-h-screen text-[#1B2A4A]">
      <Header auth={citriot?.auth} />

      <div className="max-w-[1200px] mx-auto px-6 py-7">

        {/* Page header */}
        <div className="flex justify-between items-center mb-5 animate-[fadeUp_.36s_ease_both]">

  <div className="flex items-start gap-4">

    {/* Back Button */}
    <button
      onClick={() => navigate(-1)}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg 
                 border border-[#DDE1EC] bg-white text-[13px] font-medium 
                 text-[#4A5568] transition-all 
                 hover:border-[#1B2A4A] hover:text-[#1B2A4A] hover:bg-[#E8EBF4]"
    >
      <ArrowLeftOutlined className="text-[11px]" /> Back
    </button>

    {/* Title + Subtitle */}
    <div>
      <h1 className="text-[20px] font-bold text-[#1B2A4A] leading-tight">
        Credits Wallet
      </h1>
      <p className="text-[12px] text-[#8A94A6] leading-snug">
        Manage, track and recharge your credits
      </p>
    </div>

  </div>

</div>

        {/* Tabs */}
        <div className="inline-flex gap-1 bg-white border border-[#DDE1EC] rounded-[11px] p-1 mb-6">
          {[{ key: "view", label: "Wallet Overview" }, { key: "buy", label: "Buy Credits" }].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-[22px] py-[9px] rounded-lg border-none text-[13px] font-semibold cursor-pointer transition-all
                ${tab === t.key
                  ? "bg-gradient-to-br from-[#1B2A4A] to-[#2C3E6B] text-white shadow-[0_4px_14px_rgba(27,42,74,0.3)]"
                  : "bg-transparent text-[#8A94A6] hover:bg-[#E8EBF4] hover:text-[#1B2A4A]"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ═══ OVERVIEW TAB ═══ */}
        {tab === "view" && (
          <div className="flex flex-col gap-4">

            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Balance hero */}
              <div className="relative rounded-[22px] p-[34px_36px] overflow-hidden bg-gradient-to-br from-[#1B2A4A] via-[#2C3E6B] to-[#1B3A6B] shadow-[0_20px_56px_rgba(27,42,74,.28)] flex flex-col justify-between min-h-[220px]">
                {/* Orbs */}
                <div className="absolute -top-[60px] -right-[60px] w-[240px] h-[240px] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,.08)_0%,transparent_70%)] pointer-events-none" />
                <div className="absolute -bottom-[80px] left-[5%] w-[280px] h-[280px] rounded-full bg-[radial-gradient(circle,rgba(184,134,11,.1)_0%,transparent_70%)] pointer-events-none" />

                <div className="relative z-10">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-[11px] font-semibold tracking-[0.12em] uppercase text-white/50 mb-2">Available Balance</div>
                      {loadingCredits
                        ? <Skeleton.Input active style={{ height: 46, width: 180 }} />
                        : <div className="text-[52px] font-extrabold leading-none text-white tracking-[-2px]">{availableCredits.toLocaleString()}</div>
                      }
                      <div className="text-[12px] text-white/45 mt-1.5">credits ready to use</div>
                    </div>
                    {/* Active badge */}
                    <div className="inline-flex items-center gap-[7px] bg-[rgba(184,134,11,0.18)] border border-[rgba(212,160,23,0.3)] rounded-full px-[14px] py-[5px] text-[12px] font-semibold text-[#F0C040]">
                      <span className="w-[7px] h-[7px] rounded-full bg-green-400 shadow-[0_0_0_3px_rgba(74,222,128,.25)] animate-pulse inline-block" />
                      Active
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="mt-5">
                    <div className="flex justify-between text-[11px] text-white/45 mb-[7px]">
                      <span>Credit usage</span>
                      <span className="text-[#F0C040] font-semibold">{percent}%</span>
                    </div>
                    <div className="bg-white/[0.12] rounded-full h-[5px] overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[#F0C040] to-[#D4A017] transition-[width_1s_ease]"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-[11px] text-white/35 mt-1.5">
                      <span>0</span><span>{total.toLocaleString()} total</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mini stat cards */}
              <div className="flex flex-col gap-4">
                {statCards.map((card) => (
                  <div
                    key={card.label}
                    className="bg-white border border-[#DDE1EC] rounded-[18px] px-6 py-[22px] flex-1 flex items-center gap-[18px] relative overflow-hidden transition-all duration-200 hover:shadow-[0_8px_28px_rgba(27,42,74,0.09)] hover:-translate-y-0.5 hover:border-[#b0bcd4]"
                  >
                    {/* Accent bar */}
                    <div className={`absolute left-0 top-0 bottom-0 w-[3px] rounded-l-[18px] bg-gradient-to-b ${card.accentClass}`} />
                    <div className={`w-12 h-12 rounded-[14px] ${card.iconBg} ${card.iconColor} flex items-center justify-center text-[20px] shrink-0`}>
                      {card.icon}
                    </div>
                    <div className="flex-1">
                      <div className="text-[11px] font-semibold text-[#8A94A6] uppercase tracking-[0.07em] mb-[5px]">{card.label}</div>
                      {loadingCredits
                        ? <Skeleton.Input active style={{ height: 24, width: 80 }} />
                        : <div className="text-[28px] font-extrabold text-[#1B2A4A] leading-none tracking-[-0.5px]">{card.val.toLocaleString()}</div>
                      }
                      <div className={`inline-flex items-center gap-1 text-[11px] font-semibold px-[9px] py-[2px] rounded-full mt-1.5 ${card.pctClass}`}>
                        {card.pct}
                      </div>
                    </div>
                    <RingProgress percent={card.ring} color={card.ringColor} size={52} stroke={5} />
                  </div>
                ))}
              </div>
            </div>

            {/* Row 2: stat tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
              {statTiles.map((tile) => (
                <div
                  key={tile.label}
                  className="bg-white border border-[#DDE1EC] rounded-[18px] p-5 flex items-center gap-3.5 transition-all duration-200 hover:shadow-[0_6px_22px_rgba(27,42,74,0.08)] hover:-translate-y-px"
                >
                  <div className="relative shrink-0">
                    <RingProgress percent={tile.ring} color={tile.ringColor} size={56} stroke={5} />
                    <div className="absolute inset-0 flex items-center justify-center text-[15px]" style={{ color: tile.iconColor }}>
                      {tile.icon}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold text-[#8A94A6] uppercase tracking-[0.06em] mb-1">{tile.label}</div>
                    {loadingCredits
                      ? <Skeleton.Input active style={{ height: 20, width: 60 }} />
                      : <div className="text-[20px] font-extrabold text-[#1B2A4A] tracking-[-0.5px]">{tile.value}</div>
                    }
                    <div className={`inline-flex items-center gap-1 text-[11px] font-semibold px-[9px] py-[2px] rounded-full mt-1.5 ${tile.subClass}`}>
                      {tile.sub}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Row 3: Transactions table */}
            <div>
              <div className="flex justify-between items-center mb-3.5 flex-wrap gap-2.5">
                <div>
                  <div className="text-[16px] font-bold text-[#1B2A4A]">Recent Transactions</div>
                  <div className="text-[12px] text-[#8A94A6] mt-0.5">{transactions.length} records found</div>
                </div>
                <RangePicker onChange={(v) => fetchTx(v)} allowClear style={{ borderRadius: 10 }} />
              </div>
              <Table columns={columns} dataSource={transactions} loading={tableLoading} pagination={{ pageSize: 8 }} size="middle" />
            </div>
          </div>
        )}

        {/* ═══ BUY TAB ═══ */}
        {tab === "buy" && (
          <div>
            <div className="mb-6">
              <div className="text-[20px] font-bold text-[#1B2A4A] mb-1">Choose a Credit Pack</div>
              <div className="text-[13px] text-[#8A94A6]">Instant activation · No expiry · 100% secure checkout</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {packs.map((p, i) => {
                const orig = (p.credits / 10).toFixed(2);
                const final = ((p.credits / 10) * (1 - p.discount / 100)).toFixed(2);
                const saved = ((p.credits / 10) * p.discount / 100).toFixed(2);
                return (
                  <div
                    key={p.credits}
                    className={`relative flex flex-col rounded-[22px] p-[30px_26px_26px] overflow-hidden transition-all duration-[250ms] cursor-default
                      ${p.best
                        ? "border-2 border-[#1B2A4A] shadow-[0_8px_30px_rgba(27,42,74,0.12)]"
                        : "border-2 border-[#DDE1EC]"}
                      hover:shadow-[0_16px_48px_rgba(27,42,74,0.1)] hover:-translate-y-1 hover:border-[#8A94B8] bg-white`}
                  >
                    {/* Gold top bar for best */}
                    {p.best && (
                      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#B8860B] to-[#D4A017]" />
                    )}

                    {/* Glow */}
                    <div className="absolute -top-[30px] -right-[30px] w-[160px] h-[160px] rounded-full bg-[radial-gradient(circle,rgba(27,42,74,.04)_0%,transparent_70%)] pointer-events-none" />

                    {/* Best Value ribbon */}
                    {p.best && (
                      <div className="absolute top-5 right-0 bg-gradient-to-r from-[#B8860B] to-[#D4A017] text-white text-[10px] font-bold tracking-[0.07em] uppercase px-4 py-[5px] rounded-l-lg flex items-center gap-1.5 shadow-[-4px_4px_14px_rgba(184,134,11,0.3)]">
                        <StarFilled className="text-[9px]" /> Best Value
                      </div>
                    )}

                    {/* Icon */}
                    <div className={`w-[52px] h-[52px] rounded-[14px] ${p.iconBg} ${p.iconColor} flex items-center justify-center text-[22px] mb-[18px]`}>
                      {p.icon}
                    </div>

                    <div className={`text-[11px] font-bold tracking-[0.1em] uppercase mb-1.5 ${p.tierColor}`}>{p.tier}</div>
                    <div className="text-[44px] font-extrabold text-[#1B2A4A] leading-none tracking-[-1.5px]">{p.credits.toLocaleString()}</div>
                    <div className="text-[13px] text-[#8A94A6] font-medium mt-1 mb-[18px]">credits</div>

                    <hr className="border-none border-t border-[#F0F2F8] mb-[18px]" />

                    {p.discount > 0 && (
                      <div className="text-[12px] text-[#8A94A6] line-through mb-[3px]">₹ {orig}</div>
                    )}
                    <div className="text-[30px] font-extrabold text-[#1B2A4A] tracking-[-1px]">₹ {final}</div>

                    {p.discount > 0 && (
                      <div className="inline-flex items-center gap-[5px] bg-green-50 text-green-800 border border-green-200 text-[11px] font-semibold px-2.5 py-[3px] rounded-full mt-2 self-start">
                        <CheckOutlined className="text-[9px]" /> Save ₹{saved} · {p.discount}% off
                      </div>
                    )}

                    <ul className="list-none m-0 mt-[18px] p-0 flex flex-col gap-[9px]">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-center gap-[9px] text-[13px] text-[#4A5568]">
                          <span className="w-5 h-5 rounded-full bg-slate-100 text-[#1B2A4A] flex items-center justify-center text-[9px] shrink-0">
                            <CheckOutlined />
                          </span>
                          {f}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-auto pt-[22px]">
                      <button
                        onClick={() => { setSelectedPack(p); setBuyModal(true); }}
                        className={`w-full py-3 rounded-xl border-none text-[14px] font-semibold cursor-pointer transition-all duration-200
                          ${p.best
                            ? "bg-gradient-to-br from-[#1B2A4A] to-[#2C3E6B] text-white shadow-[0_4px_18px_rgba(27,42,74,0.3)] hover:shadow-[0_8px_26px_rgba(27,42,74,0.4)] hover:-translate-y-0.5"
                            : "bg-white text-[#1B2A4A] border-2 border-[#DDE1EC] hover:bg-[#E8EBF4] hover:border-[#8A94B8]"}`}
                      >
                        Get {p.tier} Pack →
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Modal ── */}
      <Modal
        open={buyModal}
        onCancel={() => !loading && setBuyModal(false)}
        footer={null}
        centered
        styles={{
          content: { borderRadius: 22, padding: 0, overflow: "hidden", boxShadow: "0 24px 64px rgba(27,42,74,0.15)" },
          body: { padding: 0 },
        }}
        width={400}
        closable={!loading}
      >
        {selectedPack && (() => {
          const orig = (selectedPack.credits / 10).toFixed(2);
          const final = ((selectedPack.credits / 10) * (1 - selectedPack.discount / 100)).toFixed(2);
          const saved = ((selectedPack.credits / 10) * selectedPack.discount / 100).toFixed(2);
          return (
            <>
              {/* Modal hero */}
              <div className="relative bg-gradient-to-br from-[#1B2A4A] via-[#2C3E6B] to-[#1B3A6B] px-8 pt-8 text-center overflow-hidden">
                <div className="absolute -top-[50px] -right-[50px] w-[200px] h-[200px] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,.07)_0%,transparent_70%)] pointer-events-none" />
                <div className="relative z-10 w-[62px] h-[62px] rounded-[16px] bg-white/[0.12] border border-white/20 flex items-center justify-center text-[24px] text-[#F0C040] mx-auto mb-4">
                  <ThunderboltOutlined />
                </div>
                <div className="relative z-10 text-[20px] font-bold text-white mb-1">Confirm Purchase</div>
                <div className="relative z-10 text-[13px] text-white/50 pb-7">
                  {selectedPack.credits.toLocaleString()} Credits · {packs.find(p => p.credits === selectedPack.credits)?.tier} Pack
                </div>
              </div>

              {/* Modal body */}
              <div className="px-7 pt-6 pb-7 bg-white">
                <div className="bg-[#F7F8FA] border border-[#DDE1EC] rounded-[13px] overflow-hidden mb-5">
                  {selectedPack.discount > 0 && (
                    <div className="flex justify-between items-center px-4 py-3 border-b border-[#EAECF2] text-[13px] text-[#4A5568]">
                      <span>Original Price</span>
                      <span className="line-through text-[#8A94A6]">₹ {orig}</span>
                    </div>
                  )}
                  {selectedPack.discount > 0 && (
                    <div className="flex justify-between items-center px-4 py-3 border-b border-[#EAECF2] text-[13px] text-[#4A5568]">
                      <span>Discount ({selectedPack.discount}%)</span>
                      <span className="text-green-700 font-semibold">− ₹ {saved}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center px-4 py-3 text-[15px] font-bold text-[#1B2A4A]">
                    <span>Total</span><span>₹ {final}</span>
                  </div>
                </div>

                <div className="flex items-center justify-center gap-1.5 text-[12px] text-[#8A94A6] mb-5">
                  <SafetyCertificateOutlined className="text-green-700" />
                  Secure payment · Instant activation · No hidden charges
                </div>

                <div className="flex gap-2.5">
                  <button
                    onClick={() => setBuyModal(false)}
                    disabled={loading}
                    className="flex-1 h-12 rounded-xl border border-[#DDE1EC] bg-white text-[13px] font-semibold text-[#4A5568] cursor-pointer transition-all hover:bg-[#F7F8FA] disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={buyCredits}
                    disabled={loading}
                    className="flex-[2] h-12 rounded-xl border-none bg-gradient-to-br from-[#1B2A4A] to-[#2C3E6B] text-[14px] font-bold text-white cursor-pointer flex items-center justify-center gap-1.5 shadow-[0_4px_16px_rgba(27,42,74,0.3)] transition-all hover:shadow-[0_8px_22px_rgba(27,42,74,0.4)] disabled:opacity-55 disabled:cursor-not-allowed"
                  >
                    {loading
                      ? <><span className="animate-spin inline-block">⟳</span> Processing…</>
                      : <><CheckOutlined /> Confirm &amp; Pay</>
                    }
                  </button>
                </div>
              </div>
            </>
          );
        })()}
      </Modal>
    </div>
  );
}